/**
 * NavStructureService — Cosmos-driven practice nav.
 *
 * Loads NAV_STRUCTURE_V1~practice-detail at app startup via APP_INITIALIZER.
 * Once loaded, every consumer reads `get()` synchronously — sidebar, top
 * bars, dashboard column grids, and admin widget tools all derive their
 * data from the same Cosmos doc.
 *
 * If the Cosmos fetch fails (offline, dev environment, fresh deploy before
 * seed), falls back to PRACTICE_NAV_STRUCTURE in practice-detail-nav.ts so
 * the app never breaks on missing nav. The TS const stays as the canonical
 * "default" / seed source.
 *
 * Container: global-reference. Type: NAV_STRUCTURE_V1.
 * Endpoint:  GET /api/v3/nav-structures/practice-detail
 */
import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from 'environments/environment';
import {
    PRACTICE_NAV_STRUCTURE,
    PracticeNavCategory,
    PracticeNavItem,
} from './practice-detail-nav';

interface ApiResponse<T> { success: boolean; data: T; message?: string; }

interface NavStructureBodyV1 {
    id: string;
    title: string;
    description?: string;
    categories: NavCategoryV1[];
}

interface NavCategoryV1 {
    id: string;
    label: string;
    icon: string;
    color: string;
    colorHex: string;
    description?: string;
    items: NavItemV1[];
}

interface NavItemV1 {
    id: string;
    label: string;
    icon: string;
    meta: string;
    urlParams?: Record<string, string>;
    overviewWidgetId?: string;
}

interface NavStructureDocV1 {
    id: string;
    /** Type literal renamed to V4 on 2026-05-02 (interface name kept V1 for now). */
    type: 'NAV_STRUCTURE_V4';
    locationId: string;
    structureId: string;
    schemaVersion: number;
    body: NavStructureBodyV1;
    lastModifiedAt: string;
}

export type NavStructureSource = 'cosmos' | 'fallback';

@Injectable({ providedIn: 'root' })
export class NavStructureService {
    private readonly http   = inject(HttpClient);
    private readonly apiUrl = environment.apiUrl;

    private readonly _categories = signal<PracticeNavCategory[]>(PRACTICE_NAV_STRUCTURE);
    private readonly _source     = signal<NavStructureSource>('fallback');
    private readonly _lastLoaded = signal<Date | null>(null);
    private readonly _lastError  = signal<string | null>(null);

    /** Categories — synchronously available after bootstrap (or fallback). */
    readonly categories = this._categories.asReadonly();
    /** 'cosmos' if loaded from API; 'fallback' if using TS const default. */
    readonly source     = this._source.asReadonly();
    readonly lastLoaded = this._lastLoaded.asReadonly();
    readonly lastError  = this._lastError.asReadonly();

    readonly itemCount = computed(() =>
        this._categories().reduce((n, c) => n + c.items.length, 0));

    /** APP_INITIALIZER entry point. Always resolves — never blocks app startup. */
    bootstrap(): Promise<void> {
        return firstValueFrom(
            this.http.get<ApiResponse<NavStructureDocV1>>(`${this.apiUrl}/v3/nav-structures/practice-detail`).pipe(
                map(resp => {
                    const cats = resp?.data?.body?.categories;
                    if (!Array.isArray(cats) || cats.length === 0) {
                        throw new Error('Empty or invalid nav-structure body');
                    }
                    // Convert Cosmos NavCategoryV1 → PracticeNavCategory shape
                    const mapped: PracticeNavCategory[] = cats.map(c => ({
                        id:          c.id,
                        label:       c.label,
                        icon:        c.icon,
                        color:       c.color,
                        colorHex:    c.colorHex,
                        description: c.description ?? '',
                        items: (c.items ?? []).map(it => ({
                            id:               it.id,
                            label:            it.label,
                            icon:             it.icon,
                            meta:             it.meta,
                            urlParams:        it.urlParams,
                            overviewWidgetId: it.overviewWidgetId,
                        } as PracticeNavItem)),
                    }));
                    this._categories.set(mapped);
                    this._source.set('cosmos');
                    this._lastLoaded.set(new Date());
                    this._lastError.set(null);
                }),
                catchError(err => {
                    // Keep the TS-const fallback already in _categories.
                    this._source.set('fallback');
                    this._lastError.set(err?.message ?? String(err));
                    // eslint-disable-next-line no-console
                    console.warn('NavStructureService: falling back to PRACTICE_NAV_STRUCTURE TS const', err);
                    return of(undefined);
                }),
                map(() => undefined),
            ),
        );
    }

    /** Synchronous accessor — returns the loaded categories (Cosmos or fallback). */
    get(): readonly PracticeNavCategory[] {
        return this._categories();
    }

    /** Force a re-fetch (e.g. after admin edit). Falls back on error. */
    refresh(): Promise<void> {
        return this.bootstrap();
    }
}
